import { classNames } from "@/react/helpers/twMergeWithCN";
import { usePunditUserContext } from "@circle-react/contexts";
import { useRevealingMessageStore } from "../../stores/useRevealingMessageStore";
import type { ChatCardProps } from "../../types/ChatCard";
import type { ConversationStatus } from "../../types/ConversationStatus";
import type { HelpWidgetMessage } from "../../types/help_widget_message";
import { autolink } from "../../utils/autolinkUtils";
import { formatMessageAsText } from "../../utils/messageTextFormatters";
import { extractButtons, extractCards } from "../../utils/messageUtils";
import { ChatDivider } from "./ChatDivider";
import { ChatMessage } from "./ChatMessage";
import { ChatMessageFooter } from "./ChatMessageFooter";

interface MessagesProps {
  conversationId?: string;
  isBotProcessing?: boolean;
  onActionButtonClick: (message: HelpWidgetMessage, event: string) => void;
  onVoteSuccess?: (
    messageId: string,
    voteType: "upvote" | "downvote" | null,
  ) => void;
  status: ConversationStatus | null;
}

const AUTHOR_TYPE_AGENT = "agent";
const AUTHOR_TYPE_SYSTEM = "system";
const MESSAGE_TYPE_BUSINESS = "business";
const CHANNELS_WITH_FOOTER = new Set(["circle_bot", "copilot"]);

export const Messages = ({
  status,
  onActionButtonClick,
  conversationId,
  onVoteSuccess,
  isBotProcessing = false,
}: MessagesProps) => {
  const { currentCommunitySettings, isLoading: isLoadingPunditUserContext } =
    usePunditUserContext();

  const isCopilotV2Enabled =
    !isLoadingPunditUserContext && currentCommunitySettings?.copilot_v2_enabled;

  let messageGroup: string[] = [];

  const { visibleMessages, completeCurrentMessage } =
    useRevealingMessageStore();

  return (
    <div className="mt-4 flex flex-1 flex-col justify-end">
      {visibleMessages?.map((message: HelpWidgetMessage, index: number) => {
        const previousMessage = index > 0 ? visibleMessages[index - 1] : null;
        const nextMessage =
          index < visibleMessages.length - 1
            ? visibleMessages[index + 1]
            : null;
        const shouldShowAvatar =
          !previousMessage ||
          previousMessage.author_type !== message.author_type;
        const isLastMessage =
          !nextMessage || nextMessage.author_type !== message.author_type;
        const isAbsoluteLastMessage = index === visibleMessages.length - 1;
        const messageType =
          message.author_type === AUTHOR_TYPE_AGENT
            ? MESSAGE_TYPE_BUSINESS
            : message.author_type;

        // Reset group when author type changes
        if (shouldShowAvatar) {
          messageGroup = [];
        }

        const buttons = extractButtons(
          message,
          status === "closed",
          onActionButtonClick,
        );

        const shouldShowFooter =
          isCopilotV2Enabled &&
          buttons.length === 0 &&
          CHANNELS_WITH_FOOTER.has(message.channel);

        // Add current message to group
        messageGroup.push(formatMessageAsText(message));

        // Join group content for the footer
        const groupedContent = messageGroup.join("\n\n");

        return (
          <div
            key={message.id}
            className={classNames({
              "mt-5": shouldShowAvatar,
              "mt-2": !shouldShowAvatar,
            })}
          >
            {message.author_type === AUTHOR_TYPE_SYSTEM ? (
              <ChatDivider message={message} />
            ) : (
              <>
                <ChatMessage
                  key={message.id}
                  authorName={message.author_name}
                  content={autolink(message.content)}
                  channel={message.channel}
                  createdAt={message.created_at.toLocaleString()}
                  showFadingTextEffect={message.show_fading_text_effect}
                  animateUiElements={message.is_from_websocket}
                  onRevealComplete={
                    isLastMessage ? completeCurrentMessage : undefined
                  }
                  type={messageType}
                  buttons={buttons}
                  cards={extractCards(message) as ChatCardProps[]}
                  selectedButton={message.selected_button}
                  shouldShowAvatar={shouldShowAvatar}
                  showDisclaimer={false}
                  attachments={message.attachments}
                />
                {shouldShowFooter && (
                  <ChatMessageFooter
                    type={messageType}
                    isLastMessage={isLastMessage}
                    isAbsoluteLastMessage={isAbsoluteLastMessage}
                    content={groupedContent}
                    message={message}
                    conversationId={conversationId}
                    onVoteSuccess={onVoteSuccess}
                    isRevealing={message.show_fading_text_effect}
                    isBotProcessing={isBotProcessing}
                  />
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};
